import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, CheckCircle, Eye, EyeOff, Lock, ShieldAlert } from 'lucide-react';
import { Button } from '../components/Button';
import { api } from '../services/api';

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError('Link de recuperação inválido. Solicite um novo link.');
      return;
    }
    if (password.length < 8) {
      setError('A nova senha precisa ter pelo menos 8 caracteres.');
      return;
    }
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      setError('A nova senha precisa ter letras e números.');
      return;
    }
    if (password !== confirmPassword) {
      setError('As senhas não conferem.');
      return;
    }

    setLoading(true);
    try {
      const result = await api.auth.resetPassword(token, password);
      if (result?.success) {
        setSuccess(result.message || 'Senha redefinida com sucesso.');
        setPassword('');
        setConfirmPassword('');
      } else {
        setError(result?.message || 'Não foi possível redefinir a senha.');
      }
    } catch {
      setError('Erro inesperado ao redefinir a senha.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full pl-12 pr-14 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-slate-100 focus:border-slate-400 outline-none font-bold text-sm transition-all';
  const labelClass = 'text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1';

  return (
    <div className="min-h-screen bg-[#050915] text-slate-950 font-sans relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(16,185,129,.18),transparent_30%),radial-gradient(circle_at_85%_10%,rgba(59,130,246,.22),transparent_34%),linear-gradient(135deg,#050915_0%,#0f172a_48%,#052e2b_100%)]"></div>
      <main className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        <section className="w-full max-w-lg rounded-[2.5rem] bg-white shadow-2xl shadow-black/30 border border-white/70 p-6 md:p-10">
          <img src="/flowtab-logo-clean-transparent.png" alt="FlowTab" className="w-44 object-contain mb-8" />
          <div className="mb-8">
            <p className="text-[11px] font-black uppercase tracking-[0.25em] text-emerald-600 mb-3">Nova senha</p>
            <h1 className="text-3xl font-black text-slate-950 tracking-tight">Crie uma senha segura</h1>
            <p className="text-slate-500 text-sm mt-2 font-medium">Use uma senha nova para voltar a acessar o FlowTab com segurança.</p>
          </div>

          {error && (
            <div className="mb-5 p-4 bg-red-50 border border-red-100 text-red-600 text-xs font-bold rounded-2xl flex items-center gap-3">
              <ShieldAlert className="w-5 h-5 flex-shrink-0" /> {error}
            </div>
          )}
          {success && (
            <div className="mb-5 p-4 bg-emerald-50 border border-emerald-100 text-emerald-700 text-xs font-bold rounded-2xl flex items-center gap-3">
              <CheckCircle className="w-5 h-5 flex-shrink-0" /> {success}
            </div>
          )}

          {!success ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1">
                <label className={labelClass}>Nova senha</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-4 w-5 h-5 text-slate-300" />
                  <input type={showPassword ? 'text' : 'password'} className={inputClass} value={password} onChange={(event) => setPassword(event.target.value)} required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-4 text-slate-300 hover:text-slate-700">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                <label className={labelClass}>Confirmar senha</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-4 w-5 h-5 text-slate-300" />
                  <input type={showPassword ? 'text' : 'password'} className={inputClass} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required />
                </div>
              </div>
              <Button type="submit" className="w-full !mt-6 h-16 rounded-2xl text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-900/10" disabled={loading}>
                {loading ? 'Salvando...' : <span className="flex items-center gap-3">Redefinir senha <ArrowRight className="w-5 h-5" /></span>}
              </Button>
            </form>
          ) : (
            <Link to="/" className="flex h-16 w-full items-center justify-center gap-3 rounded-2xl bg-slate-950 text-xs font-black uppercase tracking-[0.2em] text-white shadow-xl shadow-slate-900/10">
              Ir para login <ArrowRight className="w-5 h-5" />
            </Link>
          )}
        </section>
      </main>
    </div>
  );
};
